import React, { useState } from "react";
import { ChevronDown, CheckCircle2 } from "lucide-react";
import PageShell, { PageHeader } from "@/components/PageShell";
import Contact from "@/components/Contact";

const FAQS = [
  { q: "Mennyi idő alatt jön ki felmérni?",
    a: "Igyekszünk hamar visszahívni, és a lehető legrövidebb időn belül kimegyünk. Sürgős esetben (beázás, viharkár) soron kívül." },
  { q: "Mennyibe kerül a felmérés?",
    a: "A felmérés ingyenes a vállalt területen — a pontos hatótávolságról kérem érdeklődjön telefonon vagy e-mailben." },
  { q: "Mennyire pontos a kapott árajánlat?",
    a: "A végszámla a tételes ajánlathoz igazodik. Csak akkor változik, ha bontás közben olyat találunk, ami felmérésnél nem volt látható — ilyenkor a folytatás előtt mindent egyeztetünk." },
  { q: "Ki készíti a munkát?",
    a: "A saját, állandó brigádunk. Nincs alvállalkozó. Minden helyszínen ott vagyunk legalább a kezdetnél és a befejezésnél." },
  { q: "Hogyan számláznak?",
    a: "A fizetési ütemezést a munka megkezdése előtt, írásban egyeztetjük. A részletek megbeszélés szerint alakulnak." },
  { q: "Mi van a garancia ideje alatt?",
    a: "A garanciaidőn belül bármilyen kivitelezési hibát térítésmentesen javítunk. A garancialevelet aláírjuk és átadjuk — nem cég-pecsétes papír." },
];

const PROCESS_AFTER_SEND = [
  { n: "01", t: "Visszahívás", b: "Telefonon egyeztetjük a felmérés időpontját, és a lehető leghamarabb kimegyünk." },
  { n: "02", t: "Helyszíni felmérés",        b: "Felmegyünk a tetőre, kibontunk egy cserepet, fotózunk. Kötelezettség nélkül." },
  { n: "03", t: "Tételes ajánlat",           b: "Tételes árajánlatot küldünk e-mailben, hogy pontosan lássa, miért fizet." },
];

function FAQItem({ q, a, idx, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={`faq-${idx}`} style={{ borderBottom: "1px solid var(--line)" }}>
      <button
        onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{
          width: "100%", background: "transparent", border: "none",
          padding: "22px 0", textAlign: "left",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          cursor: "pointer", fontFamily: "Manrope, sans-serif",
          fontWeight: 500, fontSize: 16, color: "var(--text)",
          letterSpacing: "-0.012em",
        }}
      >
        <span>{q}</span>
        <ChevronDown
          size={18} color="var(--gold)"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 200ms ease", flexShrink: 0, marginLeft: 16 }}
        />
      </button>
      {open && (
        <div className="body" style={{ paddingBottom: 22, maxWidth: 760 }}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function KapcsolatPage() {
  return (
    <PageShell conversion={false}>
      <PageHeader
        number="06"
        eyebrow="Kapcsolat"
        title="Hívjon vagy írjon."
        titleAccent="Hamarosan visszahívom."
        lead="A felmérés ingyenes. Az ajánlat tételes. Nem értékesítő érkezik — az az ember jön ki, aki a tetőn is fenn lesz."
      />

      <Contact showHeader={false} />

      {/* What happens next — 3-step process card */}
      <section className="section bg-grid" style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", position: "relative" }}>
        <div className="site-container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
            <span className="eyebrow">Mi történik ezután</span>
          </div>
          <h2 className="h2" style={{ margin: 0, marginBottom: 56, maxWidth: 720 }}>
            A megkeresés után <span style={{ color: "var(--gold)" }}>három lépés.</span>
          </h2>

          <style>{`
            .nextstep-grid { grid-template-columns: 1fr; }
            @media (min-width: 900px) { .nextstep-grid { grid-template-columns: repeat(${PROCESS_AFTER_SEND.length}, 1fr); } }
          `}</style>
          <div className="nextstep-grid" style={{
            display: "grid",
            gap: 1, background: "var(--line)", border: "1px solid var(--line)",
          }}>
            {PROCESS_AFTER_SEND.map((p, i) => (
              <div key={p.n} data-testid={`next-step-${i}`} style={{ background: "var(--ink-2)", padding: 32 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <CheckCircle2 size={18} color="var(--gold)" strokeWidth={1.6} />
                  <span className="font-display" style={{ fontSize: 13, color: "var(--gold)", letterSpacing: "0.04em" }}>
                    {p.n}
                  </span>
                </div>
                <h3 className="h3" style={{ margin: "0 0 10px" }}>{p.t}</h3>
                <p className="body" style={{ margin: 0 }}>{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" style={{ background: "var(--ink)" }}>
        <div className="site-container">
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
            <span className="eyebrow">Gyakori kérdések</span>
          </div>
          <h2 className="h2" style={{ margin: 0, marginBottom: 32, maxWidth: 720 }}>
            Kérdések, amiket <span style={{ color: "var(--gold)" }}>valóban</span> kérdeznek.
          </h2>

          <div style={{ maxWidth: 860, borderTop: "1px solid var(--line)" }}>
            {FAQS.map((f, i) => <FAQItem key={i} idx={i} q={f.q} a={f.a} defaultOpen={i === 0} />)}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
